import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CHIP } from '../ui/ModuleSection';
import { MANAGEMENT_ITEMS, NAV_ITEMS, type NavItem } from './navigation';

/**
 * DESIGN.md §Layout and §Navigation.
 *
 * One component, two expressions of one table: a fixed bottom bar under `lg`
 * (56px plus the safe-area inset, six items, icon over a 12px label) and a
 * 240px rail from `lg` up, same six destinations in the same order. Which one
 * is seen is a media query's decision; both are always rendered, which is why
 * a test finds every link twice.
 *
 * **Nothing here animates a tab change.** A student taps these dozens of times
 * a day, and on a path that frequent an animation is not polish, it is a delay
 * charged per tap. Only the active chip's background fades, over 160ms.
 *
 * The active tab wears its module's pastel (DESIGN.md §Tertiary: "the one
 * piece of colour a student learns without being taught"). The inactive ones
 * wear nothing - six pastels lit at once is wallpaper, not wayfinding.
 */
export function TabBar({
  variant,
  canManage,
}: {
  /**
   * Which expression to render. The shell places them in different parts of
   * the tree - the rail inside the sticky `<aside>`, the bar fixed to the
   * viewport - so one component that rendered both would have to be mounted
   * twice, and each would render the other invisibly.
   */
  variant: 'bar' | 'rail';
  canManage: boolean;
}) {
  const { t } = useTranslation();

  const barItem = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `flex-1 min-w-0 min-h-[56px] flex flex-col items-center justify-center gap-0.5 px-1 ${
          isActive ? 'text-on-surface' : 'text-on-surface-variant'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`w-12 h-7 rounded-full flex items-center justify-center transition-colors duration-chip ${
              isActive && item.module ? CHIP[item.module] : ''
            }`}
          >
            <span className="material-symbols-outlined text-[22px]">{item.icon}</span>
          </span>
          {/*
            `truncate` and not a fixed width: French runs 15-20% longer than
            English (NFR04, The Two Language Widths Rule) and "Covoiturage" does
            not fit a sixth of 390px. The label may shorten; it never wraps and
            never pushes a neighbour off the bar.
          */}
          <span className="font-label-sm text-label-sm truncate max-w-full">
            {t(item.labelKey)}
          </span>
        </>
      )}
    </NavLink>
  );

  const railItem = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 min-h-[44px] rounded-full font-body-md text-body-md transition-colors duration-chip ${
          isActive
            ? 'bg-primary text-on-primary font-medium'
            : 'text-on-surface/75 hover:text-on-surface hover:bg-primary/[0.06]'
        }`
      }
    >
      <span className="material-symbols-outlined text-[22px]">{item.icon}</span>
      {t(item.labelKey)}
    </NavLink>
  );

  /*
    The phone bar. Overlay shadow pointing up (DESIGN.md §Elevation), and the
    safe-area inset as padding rather than height, so the bar still measures a
    full 56px of touch target on a notched device.
  */
  if (variant === 'bar') {
    return (
      <nav
        aria-label={t('nav.modules')}
        className="lg:hidden fixed inset-x-0 bottom-0 z-40 flex items-stretch bg-surface-container-lowest/95 backdrop-blur border-t border-hairline pb-[env(safe-area-inset-bottom,0px)]"
      >
        {NAV_ITEMS.map(barItem)}
      </nav>
    );
  }

  /* The desktop rail: the same six, in the same order, plus management. */
  return (
    <nav aria-label={t('nav.modules')} className="flex flex-col gap-1 px-3 flex-1 overflow-y-auto">
      {NAV_ITEMS.map(railItem)}
      {canManage && (
        <>
          <div className="my-3 border-t border-outline-variant/30 mx-2" />
          <div className="px-4 py-1.5 font-label-sm text-label-sm text-on-surface-variant/60">
            {t('nav.management')}
          </div>
          {MANAGEMENT_ITEMS.map(railItem)}
        </>
      )}
    </nav>
  );
}
